'use client';

import { useState } from 'react';

type SelectionGroupOptions<T> = {
  data: Array<T>;
  min?: number;
  max?: number;
  onChange: (items: T[]) => void;
  selected: T[];
  name?: string;
  itemEquals?: (item: T, datum: T) => boolean;
};

export type ItemProps<T> = {
  component: 'RADIO' | 'CHECK';
  index: number;
  disabled: boolean;
  onChange: () => void;
  checked: boolean;
  data: T;
  min?: number;
  max?: number;
  name: string;
};

export function useSelectionGroup<T>({
  min,
  max,
  data,
  selected: selectedProp,
  onChange: onChangeProp,
  name: nameProp,
  itemEquals = (item, datum) => item === datum,
}: SelectionGroupOptions<T>) {
  const asRadios = min === max && min === 1; // in the min = max = 1 case, show radio buttons, which are always enabled
  const [name] = useState(nameProp ?? Math.random().toString());
  const numSelected = selectedProp.length;
  const criteriaMet = (!min || numSelected >= min) && (!max || numSelected <= max);

  const options = data.map((datum, index) => ({
    props: {
      component: asRadios ? 'RADIO' : 'CHECK',
      data: datum,
      index,
      disabled: Boolean(
        !selectedProp.some((item) => itemEquals(item, datum)) &&
        max &&
        max <= numSelected &&
        !asRadios,
      ),
      onChange: () =>
        onChangeProp(
          asRadios
            ? [datum]
            : !selectedProp.some((item) => itemEquals(item, datum))
              ? [...selectedProp, datum]
              : selectedProp.filter((item) => !itemEquals(item, datum)),
        ),
      checked: selectedProp.some((item) => itemEquals(item, datum)),
      min,
      max,
      name,
    } as ItemProps<T>,
  }));

  return { min, max, options, criteriaMet, numSelected };
}
